import React, { useState, useEffect, useRef } from "react";
import { TransformWrapper, TransformComponent, useTransformContext } from "react-zoom-pan-pinch";
import "./CustomSeatLayout.css";

const CustomSeatLayout = ({
  seatLayout,
  bookedSeats = [],
  selectedSeats = [],
  lockedSeats = [],
  pendingSeats = [],
  onSeatClick,
  pricing = { silver: 150, gold: 200, premium: 300 },
}) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const containerRef = useRef(null);
  const [isMoving, setIsMoving] = useState(false);
  const movementTimeoutRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getSeatId = (row, seatNumber) => `${row}-${seatNumber}`;

  const getSeatStatus = (row, seatNumber) => {
    const seatId = getSeatId(row, seatNumber);
    if (bookedSeats.includes(seatId)) return "booked";
    if (pendingSeats.includes(seatId)) return "pending";
    if (selectedSeats.some((s) => s.id === seatId)) return "selected";
    if (lockedSeats.includes(seatId)) return "locked";
    return "available";
  };

  const getSeatClass = (rowObj, rowIndex) => {
    if (rowObj.class) return rowObj.class;
    const totalRows = seatLayout.length;
    if (rowIndex < 2) return "premium";
    return rowIndex < 5 ? "gold" : "silver";
  };

  const getSeatPrice = (seatClass, rowObj) => {
    if (rowObj && rowObj.price) return rowObj.price;
    return pricing[seatClass] || pricing.silver;
  };

  const handleSeatClick = (row, seatNumber, seatClass, rowObj) => {
    if (seatNumber === null) return;
    const seatId = getSeatId(row, seatNumber);
    const status = getSeatStatus(row, seatNumber);
    if (status === "booked" || status === "locked" || status === "pending")
      return;

    const seatData = {
      id: seatId,
      row,
      number: seatNumber,
      price: getSeatPrice(seatClass, { price: rowObj?.price }),
      type: rowObj?.section || seatClass,
      class: seatClass,
    };
    onSeatClick(seatData);
  };

  const handleTransform = () => {
    setIsMoving(true);
    if (movementTimeoutRef.current) clearTimeout(movementTimeoutRef.current);
    movementTimeoutRef.current = setTimeout(() => {
      setIsMoving(false);
    }, 1500);
  };

  if (!seatLayout || seatLayout.length === 0) return null;

  const sections = [];
  let currentSec = null;
  seatLayout.forEach((rowObj, rowIndex) => {
    const seatClass = getSeatClass(rowObj, rowIndex);
    if (rowObj.seats.filter(s => s !== null).length === 0) return;
    const baseLabel = rowObj.section ? rowObj.section.toUpperCase() : (rowIndex < 2 ? "PLATINUM" : rowIndex < 5 ? "GOLD" : "SILVER");
    const sectionPrice = getSeatPrice(seatClass, rowObj);
    const sectionKey = `${baseLabel}-${sectionPrice}`;
    if (!currentSec || currentSec.key !== sectionKey) {
      currentSec = { key: sectionKey, label: baseLabel, price: sectionPrice, rows: [] };
      sections.push(currentSec);
    }
    currentSec.rows.push({ ...rowObj, rowIndex });
  });

  const renderLayout = () => (
    <div className="district-canvas-content">
      {sections.map((section, sIdx) => (
        <div key={sIdx} className="district-layout-section">
          <div className="district-section-divider">
            {section.label} : ₹{section.price}
          </div>
          <div className="district-rows-wrapper">
            {section.rows.map((rowObj, rIdx) => (
              <div key={rIdx} className="district-layout-row">
                <div className="district-row-name">{rowObj.row}</div>
                <div className="district-seats-strip">
                  {rowObj.seats.map((seat, seatIdx) => {
                    if (seat === null) return <div key={seatIdx} className="district-seat-gap" />;
                    const status = getSeatStatus(rowObj.row, seat);
                    const isBooked = ["booked", "locked", "pending"].includes(status);
                    const isSelected = status === "selected";
                    const isAcc = Number(seat) >= 1000;
                    return (
                      <div
                        key={seatIdx}
                        className={`district-seat-box ${status}`}
                        onClick={() => !isBooked && handleSeatClick(rowObj.row, seat, section.class, rowObj)}
                      >
                        {status === "booked" ? "×" : (isAcc ? "♿" : seat)}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="district-screen-indicator-fixed">
        <img
          src="https://cdn.district.in/movies-web/_next/static/media/screen-img-light.b7b18ffd.png"
          alt="Screen"
          className="district-screen-img"
        />
        <div className="screen-indicator-text">SCREEN THIS WAY</div>
      </div>
    </div>
  );

  return (
    <div className="district-booking-container" ref={containerRef}>
      {isDesktop ? (
        <div className="desktop-canvas-wrapper">{renderLayout()}</div>
      ) : (
        <TransformWrapper
          initialScale={0.6}
          minScale={0.1}
          maxScale={4}
          centerOnInit={true}
          onTransformed={handleTransform}
          limitToBounds={false}
        >
          <LayoutAutoCenter />
          <TransformComponent
            wrapperStyle={{ width: "100%", height: "100%" }}
            contentStyle={{ padding: "80px" }}
          >
            {renderLayout()}
          </TransformComponent>
          <SeatMiniMap sections={sections} selectedSeats={selectedSeats} isVisible={isMoving} />
        </TransformWrapper>
      )}
    </div>
  );
};

const LayoutAutoCenter = () => {
  const context = useTransformContext();
  useEffect(() => {
    if (!context) return;
    const centerView = context.centerView || (context.instance && context.instance.centerView);
    if (typeof centerView === 'function') {
      setTimeout(() => centerView(0.6, 0), 200);
    }
  }, [context]);
  return null;
};

const SeatMiniMap = ({ sections, selectedSeats, isVisible }) => {
  const context = useTransformContext();
  if (!context) return null;
  const { scale = 0.6, positionX = 0, positionY = 0 } = context.state || context;

  return (
    <div className={`district-minimap-ui ${isVisible ? 'visible' : ''}`}>
      <div className="minimap-scaler">
        {sections.map((sec, sIdx) => (
          <div key={sIdx} className="mm-section">
            {sec.rows.map((row, rIdx) => (
              <div key={rIdx} className="mm-row">
                {row.seats.map((s, idx) => (
                  <div key={idx} className={`mm-dot ${s === null ? 'hide' : ''} ${selectedSeats.some(sel => sel.id === `${row.row}-${s}`) ? 'selected' : ''}`} />
                ))}
              </div>
            ))}
          </div>
        ))}
        <div 
          className="mm-viewport" 
          style={{
            width: `${Math.max(20, 70 / scale)}%`,
            height: `${Math.max(20, 70 / scale)}%`,
            left: `${50 - (positionX / (18 * scale)) - 35}%`,
            top: `${50 - (positionY / (18 * scale)) - 35}%`
          }}
        />
      </div>
    </div>
  );
};

export default CustomSeatLayout;
